import { isOtherBoutiqueCredit } from "@/features/invoices/credit-invoices/credit-invoice.type";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import React, { useCallback, useMemo } from "react";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useGetDenseProformaInvoice, {
  GetDenseProformaInvoiceParameter,
  GetDenseProformaInvoiceRequest,
} from "@/features/invoices/proforma-invoices/hooks/use-get-dense-proforma-invoice";
import useIdentity from "@/features/ui/hooks/use-identity";
import { Formik } from "formik";
import { ProformaInvoiceFormFields } from "@/features/invoices/proforma-invoices/upload-proforma-invoice-page";
import { Grid } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import ProformaInvoiceForm from "@/features/invoices/proforma-invoices/proforma-invoice-form";
import { useProformaInvoiceDumbTransaction } from "@/features/invoices/proforma-invoices/hooks/use-proforma-invoice-dumb-transaction";
import { isStoredObject, Price } from "@/features/types";
import useUpdateProformaInvoice, {
  UpdateProformaInvoiceRequest,
} from "@/features/invoices/proforma-invoices/hooks/use-update-proforma-invoice";
import useRequiredParams from "@/features/invoices/hooks/use-required-params";
import useProformaInvoiceFormValidationSchema from "@/features/invoices/proforma-invoices/hooks/use-proforma-invoice-form-validation-schema";
import useReadResource from "@/features/invoices/proforma-invoices/hooks/use-read-resource";
import useReadProformaInvoice from "@/features/invoices/proforma-invoices/hooks/use-read-proforma-invoice";
import InvoiceDetailPageHeader from "@/features/invoices/bank-remittance-receipts/invoice-detail-page-header";
import PriceUtils from "@/features/ui/utils/price-utils";
import {
  proformaRequiredAmounts,
  remainingDepositOf,
  resolveTotalRequiredAmount,
  storedTotalRequiredAmount,
} from "@/features/invoices/proforma-invoices/cross-trade-fee";
import useDumbMixedUpdate from "@/features/transportations/use-dumb-mixed-update";

interface ProformaInvoiceDetailPageProps {
  isEditing?: boolean;
}

export default function ProformaInvoiceDetailPage({
  isEditing = false,
}: ProformaInvoiceDetailPageProps) {
  const { t, tTitle } = useI18nHelper();
  const { error: showError } = useAppToasts();
  const identity = useIdentity();
  const { proformaInvoiceId } = useRequiredParams<{
    proformaInvoiceId: number;
  }>();
  const { navigate } = useRouterHelper();

  const getDenseProformaInvoiceKey = useMemo(():
    | {
        parameter: GetDenseProformaInvoiceParameter;
        request: GetDenseProformaInvoiceRequest;
      }
    | undefined => {
    if (identity?.company && proformaInvoiceId) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
          proformaInvoiceId,
        },
        request: {},
      };
    }
    return undefined;
  }, [identity, proformaInvoiceId]);

  const {
    data: proformaInvoice,
    error: getProformaInvoiceError,
    mutate: mutateGetProformaInvoice,
  } = useGetDenseProformaInvoice(
    getDenseProformaInvoiceKey?.parameter,
    getDenseProformaInvoiceKey?.request,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );

  const initialValues = useMemo((): ProformaInvoiceFormFields => {
    if (proformaInvoice) {
      // 프로포마 금액 통화 — 서버가 내려준 합계 통화(원화 프로포마가 아니면 유로)
      const currency: Price["currency"] =
        proformaInvoice.totalAmount?.currency === "KRW" ? "KRW" : "EUR";
      // 잔여 디파짓은 OC 헤더 기준이다(브랜드별 디파짓은 더 이상 쓰지 않는다, 2026-10-30).
      // 수정 화면에서는 이 프로포마가 쓴 디파짓을 되더한다(서버가 되돌린 뒤 다시 깎는다).
      const ownUsedDeposit = isEditing ? proformaInvoice.usedDeposit : undefined;
      const orderConfirmationInvoices = proformaInvoice.orderConfirmationInvoices.map(
        (ocInvoice) => {
          const ownUsedDepositOfOc = PriceUtils.sum(
            proformaInvoice.proformaInvoiceDetailList
              .filter((pDetail) =>
                ocInvoice.orderConfirmationInvoiceDetailList.some(
                  (ocDetail) => ocDetail.id === pDetail.orderConfirmationInvoiceDetail?.id
                )
              )
              .map((pDetail) => pDetail.usedDepositAmount),
            currency
          );
          return {
            ...ocInvoice,
            expectedRemainingDepositAmount: PriceUtils.placeholder(
              PriceUtils.sum(
                [
                  ocInvoice.expectedRemainingDepositAmount,
                  isEditing ? ownUsedDepositOfOc : undefined,
                ],
                currency
              ),
              currency
            ) as Price,
          };
        }
      );
      const expectedRemainingDepositAmount = remainingDepositOf(
        orderConfirmationInvoices,
        currency,
        ownUsedDeposit
      );

      // 브랜드 행은 보여 주기만 한다(저장은 서버가 총액 비율로 다시 만든다)
      const clientSideProformaInvoiceDetailList: ProformaInvoiceFormFields["proformaInvoiceDetailList"] =
        proformaInvoice.proformaInvoiceDetailList.map((pDetail) => ({
          ...pDetail,
          orderConfirmationInvoiceDetail: undefined,
          orderSheetSet: pDetail.orderSheetSet ? { id: pDetail.orderSheetSet.id } : undefined,
        }));

      const values: ProformaInvoiceFormFields = {
        id: proformaInvoice.id,
        currency,
        name: proformaInvoice.name,
        season: proformaInvoice.season,
        status: proformaInvoice.status,
        lineSheetSetType: proformaInvoice.lineSheetSetType || undefined,
        usedDeposit: proformaInvoice.usedDeposit,
        totalAmount: proformaInvoice.totalAmount,
        orderedBy: proformaInvoice.issuedToCompany,
        isRemittedToBoutique: proformaInvoice.isRemittedToBoutique,
        isRemittedToAgency: proformaInvoice.isRemittedToAgency,
        file: proformaInvoice.file || undefined,
        expectedRemainingDepositAmount,

        issuedThrough: proformaInvoice.issuedThroughCompany,
        issuedBy: proformaInvoice.issuedByCompany,
        originallyIssuedBy: proformaInvoice.originallyIssuedByCompany,
        issuedOn: proformaInvoice.issuedOn,
        paymentMethod: proformaInvoice.paymentMethod,
        paidByAgency: proformaInvoice.paidBy === "AGENCY",
        isBuyerToAgencyEur: proformaInvoice.isBuyerToAgencyEur || false,
        crossTradeFeeRate: proformaInvoice.crossTradeFeeRate ?? undefined,
        exposedToBuyer: proformaInvoice.isPublic,
        isDetailsEditedManually:
          proformaInvoice.isDetailsEditedManually || false,
        usedDepositComment: proformaInvoice.usedDepositComment || undefined,
        usedCreditInvoices: proformaInvoice.usedCreditInvoices.map(
          (cInvoice) => {
            return {
              id: cInvoice.id,
              name: cInvoice.name,
              amount: cInvoice.amount,
              season: cInvoice.season,
              ownedByCompany: cInvoice.ownedByCompany,
              issuedToCompany: cInvoice.issuedToCompany,
              issuedByCompany: cInvoice.issuedByCompany,
            };
          }
        ),
        // 이미 타부티크 크레딧이 붙어 있으면 그 선택지를 켠 채로 연다.
        usesOtherBoutiqueCredit: proformaInvoice.usedCreditInvoices.some(
          (cInvoice) =>
            isOtherBoutiqueCredit(cInvoice, {
              boutiqueIds: [
                proformaInvoice.issuedByCompany?.id,
                proformaInvoice.originallyIssuedByCompany?.id,
              ],
              agencyId: proformaInvoice.issuedThroughCompany?.id,
            })
        ),
        usedCreditComment: proformaInvoice.usedCreditComment || undefined,
        orderConfirmationInvoices,
        orderSheetSets: proformaInvoice.orderSheetSets,
        proformaInvoiceDetailList: clientSideProformaInvoiceDetailList,
        childProformaInvoices: proformaInvoice.childProformaInvoices.map(
          (pInvoice) => {
            return {
              id: pInvoice.id,
              orderConfirmationInvoices: pInvoice.orderConfirmationInvoices.map(
                (ocInvoice) => {
                  return {
                    id: ocInvoice.id,
                  };
                }
              ),
              orderSheetSets: pInvoice.orderSheetSets.map((oss) => {
                return {
                  id: oss.id,
                };
              }),
            };
          }
        ),
        parentProformaInvoices: proformaInvoice.parentProformaInvoices.map(
          (pInvoice) => {
            return {
              id: pInvoice.id,
            };
          }
        ),

        number: proformaInvoice.number || undefined,
        privateComment: proformaInvoice.privateComment || undefined,
        publicComment: proformaInvoice.publicComment || undefined,
        earnedCreditInvoices: proformaInvoice.earnedCreditInvoices,
        attachments: proformaInvoice.attachments,
        payments: proformaInvoice.payments,
      };
      // 저장된 송금 필요 금액이 자동 계산값과 다르면(담당자가 인보이스에 맞춰 고친 값) 직접 입력값으로 싣는다
      return {
        ...values,
        totalRequiredAmount: storedTotalRequiredAmount(
          proformaInvoice.totalRequiredAmount,
          proformaRequiredAmounts(values, currency).requiredAmount
        ),
      };
    } else {
      return {
        isDetailsEditedManually: false,
        proformaInvoiceDetailList: [],
        orderSheetSets: [],
        orderConfirmationInvoices: [],
        usedCreditInvoices: [],
        exposedToBuyer: identity?.company?.type !== "BOUTIQUE",
        paidByAgency: false,
        issuedOn: new Date(),
        childProformaInvoices: [],
        parentProformaInvoices: [],
        earnedCreditInvoices: [],
        payments: [],
        attachments: [],
      };
    }
  }, [proformaInvoice, isEditing, identity?.company?.type]);

  const validateSchema = useProformaInvoiceFormValidationSchema();

  const validate = useCallback(
    async (values: ProformaInvoiceFormFields) => {
      try {
        await validateSchema.validate(values, { abortEarly: false });
      } catch (e) {
        showError(e);
        return e;
      }
    },
    [validateSchema, showError]
  );

  const {
    fire: fireUpdateProformaInvoice,
    error: updateProformaInvoiceError,
    clear: clearUpdateProformaInvoice,
  } = useUpdateProformaInvoice();

  const { isUpdateQueued, handleSubmit } = useDumbMixedUpdate({
    dumbFunction: useProformaInvoiceDumbTransaction,
    smartFunction: useUpdateProformaInvoice,
    isSmart: (request): request is UpdateProformaInvoiceRequest => {
      return (
        request.file &&
        isStoredObject(request.file) &&
        request.attachments.every(isStoredObject)
      );
    },
    toRequest: (values: ProformaInvoiceFormFields) => {
      const currency = values.currency ?? "EUR";
      const totalUsedCredit = PriceUtils.sum(
        values.usedCreditInvoices.map((ocInvoice) => ocInvoice.amount),
        currency
      );
      // 브랜드별 금액은 보내지 않는다 — 서버가 총액·사용 디파짓을 연결 오더·OC 비율로 나눠 저장한다(2026-10-30)
      const { amount: totalAmount, requiredAmount } = proformaRequiredAmounts(
        values,
        currency
      );

      return {
        id: values.id!,
        number: values.number!,
        season: values.season!,
        name: values.name!,
        status: values.status!,
        lineSheetSetType: values.lineSheetSetType || null,
        issuedToCompany: values.orderedBy!,
        issuedByCompany: values.issuedBy!,
        issuedThroughCompany: values.issuedThrough!,
        issuedOn: values.issuedOn!,
        paymentMethod: values.paymentMethod!,
        paidBy: values.paidByAgency ? ("AGENCY" as const) : ("BUYER" as const),
        isBuyerToAgencyEur: values.paidByAgency && !!values.isBuyerToAgencyEur,
        isPublic: values.exposedToBuyer,
        publicComment: values.publicComment || null,
        privateComment: values.privateComment || null,
        usedCreditInvoices: values.usedCreditInvoices.map((cInvoice) => {
          return {
            id: cInvoice.id,
          };
        }),
        orderSheetSets: values.orderSheetSets.map((oss) => {
          return {
            id: oss.id,
          };
        }),
        orderConfirmationInvoices: values.orderConfirmationInvoices.map(
          (ocInvoice) => {
            return {
              id: ocInvoice.id,
            };
          }
        ),
        usedDeposit: PriceUtils.placeholder(values.usedDeposit, currency),
        usedDepositComment: values.usedDepositComment || null,
        usedCredit: PriceUtils.placeholder(totalUsedCredit, currency),
        usedCreditComment: values.usedCreditComment || null,
        isDetailsEditedManually: false,
        totalAmount: PriceUtils.placeholder(totalAmount, currency),
        totalRequiredAmount: PriceUtils.placeholder(
          resolveTotalRequiredAmount(values.totalRequiredAmount, requiredAmount),
          currency
        ),
        proformaInvoiceDetailList: [],
        originallyIssuedByCompany: values.originallyIssuedBy!,
        childProformaInvoices: values.childProformaInvoices.map((pInvoice) => {
          return {
            id: pInvoice.id,
          };
        }),
        file: values.file!,
        attachments: values.attachments,
        exchangeRate: values.exchangeRate || null,
        bankFee: PriceUtils.placeholder(values.bankFee, "KRW"),
      };
    },
    mutate: mutateGetProformaInvoice,
    defaultRedirectUrl: `/proforma-invoices/${proformaInvoiceId}`,
  });

  useReadResource({
    ywrFunc: useReadProformaInvoice,
    resource: proformaInvoice,
    when: () => identity?.company?.type === "BUYER",
    mutate: mutateGetProformaInvoice,
  });

  return (
    <Formik<ProformaInvoiceFormFields>
      initialValues={initialValues}
      validate={validate}
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
      enableReinitialize={true}
      onSubmit={async (values, formikHelpers) => {
        if (identity?.company) {
          return handleSubmit(
            {
              by: identity.company.type,
              companyId: identity.company.id,
              proformaInvoiceId: values.id!,
            },
            values,
            formikHelpers
          );
        } else {
          return false;
        }
      }}
    >
      {(props) => {
        return (
          <Grid
            as={"form"}
            height={"100%"}
            width={"100%"}
            gridTemplateColumns={"1fr"}
            gridTemplateRows={"auto auto 1fr"}
          >
            <InvoiceDetailPageHeader
              formik={props}
              isUpdateQueued={
                identity?.company && props.values.id
                  ? isUpdateQueued({
                      by: identity.company.type,
                      companyId: identity.company.id,
                      proformaInvoiceId: props.values.id,
                    })
                  : false
              }
              title={tTitle("proforma_invoice")}
              baseUrl={"/proforma-invoices"}
              isEditing={isEditing}
            />

            <ProformaInvoiceForm formik={props} isEditing={isEditing} />
          </Grid>
        );
      }}
    </Formik>
  );
}
